import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";

export const GetTranslationData = (resourceType, resourceKey) => {
  const translationData = useSelector((state) => state.translationReducer);

  if (!Array.isArray(translationData?.data)) return "";

  // Find client-specific translation first (non-zero clientId)
  const clientSpecific = translationData.data.find(
    (item) =>
      item.clientId !== 0 &&
      item.resourceType === resourceType &&
      item.resourceKey === resourceKey
  );

  if (clientSpecific) return clientSpecific.resourceValue;

  // Fallback to default client (clientId === 0)
  const defaultTranslation = translationData.data.find(
    (item) =>
      item.clientId === 0 &&
      item.resourceType === resourceType &&
      item.resourceKey === resourceKey
  );

  return defaultTranslation ? defaultTranslation.resourceValue : "";
};

const emptyLookup = () => "";

const addFirstMatch = (index, item) => {
  let byKey = index.get(item.resourceType);
  if (!byKey) {
    byKey = new Map();
    index.set(item.resourceType, byKey);
  }
  // Keep the first row only, matching Array.find semantics of GetTranslationData.
  if (!byKey.has(item.resourceKey)) byKey.set(item.resourceKey, item.resourceValue);
};

// Same resolution rules as GetTranslationData (client-specific row first, then
// clientId 0 row, else ""), but indexed once per translation payload instead of
// scanning the whole array for every key on every render.
export const selectTranslationLookup = createSelector(
  [(state) => state.translationReducer?.data],
  (data) => {
    if (!Array.isArray(data)) return emptyLookup;

    const clientSpecific = new Map();
    const defaults = new Map();
    data.forEach((item) => {
      if (!item) return;
      if (item.clientId !== 0) addFirstMatch(clientSpecific, item);
      else addFirstMatch(defaults, item);
    });

    return (resourceType, resourceKey) => {
      const clientRows = clientSpecific.get(resourceType);
      if (clientRows?.has(resourceKey)) return clientRows.get(resourceKey);

      const defaultRows = defaults.get(resourceType);
      return defaultRows?.has(resourceKey) ? defaultRows.get(resourceKey) : "";
    };
  }
);
